import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useAnchoredPanel } from "../use-anchored-panel";
import { Icon } from "./Icon";

/**
 * Infobulle DS (§6.14) : une explication attachée à l'élément qu'elle explique,
 * qui s'ouvre AU DOIGT — jamais un attribut `title`, invisible sur un téléphone
 * faute de survol.
 *
 * Patron « disclosure » : bouton 44×44 (`aria-expanded` / `aria-controls`) +
 * panneau flottant `.ds-popover` (§6.12, borné par l'écran visible via
 * `useAnchoredPanel`). Se referme au second toucher, au toucher ailleurs, ou
 * avec Échap.
 *
 * Quand l'utiliser : ajouter un DÉTAIL à côté d'une information déjà visible
 * (le sort de l'audio, à côté du lecteur). Jamais pour porter seule une
 * information essentielle — ce qui doit être lu reste à l'écran.
 */
interface InfoTipProps {
  /** Nom accessible du bouton : ce que l'infobulle explique, en mots
   * (« Ce qui arrive à cet audio ») — pas « Info » ni « Aide ». */
  label: string;
  /** L'explication : du texte, ou quelques phrases. */
  children: ReactNode;
}

export function InfoTip({ label, children }: InfoTipProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const tipId = useId();
  const { panelRef, side } = useAnchoredPanel(open);

  // Toucher ailleurs referme (même patron que les autres flottants du DS :
  // EditableTypeBadge, PersonPicker) — aucune bulle coincée à l'écran.
  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-flex">
      {/* Échap est écouté sur le BOUTON, pas sur le panneau : celui-ci n'est pas
          focalisable (une bulle explicative n'a rien à focaliser), le focus
          reste donc sur le bouton pendant toute l'ouverture — et il n'y a rien
          à lui rendre à la fermeture. */}
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? tipId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
        className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-md text-muted transition-colors hover:text-flambeau-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
      >
        <Icon name="circle-question" size={16} />
      </button>
      {open && (
        <div
          id={tipId}
          ref={panelRef}
          className={`ds-popover ds-popover--${side} rounded-md border border-line bg-surface p-3 text-small text-ink shadow-float`}
        >
          {children}
        </div>
      )}
    </div>
  );
}
